import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { z } from 'zod';
import confIni from '../conf.json';
import type { TranslationKey, TranslationMap } from './translation-map';
import translations from './translations';

export const languageSchema = z.union([z.literal('sv'), z.literal('en'), z.literal('fi')]);

export type Lang = z.infer<typeof languageSchema>;

type Locale = (typeof LOCALE)[keyof typeof LOCALE]['code'];

const LOCALE: {
  [key in Lang]: {
    code: string;
    name: string;
  };
} = {
  sv: {
    code: 'sv-SE',
    name: 'Svenska',
  },
  en: {
    code: 'en-GB',
    name: 'English',
  },
  fi: {
    code: 'fi-FI',
    name: 'Suomi',
  },
} as const;

const isServer = typeof window === 'undefined';
const savedLanguage =
  !isServer && localStorage && localStorage.getItem('language') ? localStorage.getItem('language') : null;

const availableLangs = [...Object.keys(translations)];

if (savedLanguage && !availableLangs.includes(savedLanguage)) {
  localStorage.setItem('language', 'sv');
}

const DETECTION_OPTIONS = {
  lookupLocalStorage: 'language',
  lookupCookie: 'i18next',
  order: ['cookie', 'localStorage'],
  caches: ['cookie', 'localStorage'],
  cookieMinutes: 60 * 24 * 365,
  ...(confIni?.cookieDomain ? { cookieDomain: confIni.cookieDomain } : {}),
  cookieOptions: { path: '/', sameSite: 'lax' as const, secure: true },
};

const resources = {
  en: { translations: translations['en'] },
  sv: { translations: translations['sv'] },
  fi: { translations: translations['fi'] },
};

i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .use({
    type: 'postProcessor',
    name: 'capitalize',
    process: (value: string) => {
      if (typeof value !== 'string') return value;
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
  })
  .use({
    type: 'postProcessor',
    name: 'lowercase',
    process: (value: string) => {
      if (typeof value !== 'string') return value;
      return value.toLowerCase();
    },
  })
  .init({
    resources,
    detection: DETECTION_OPTIONS,
    fallbackLng: 'sv',
    ns: ['translations'],
    defaultNS: 'translations',
    react: {
      useSuspense: false,
    },
    returnObjects: true,
  });

/**
 * _t is a typesafe translation function that ensures the key exists in lang json
 */
const _t = <K extends TranslationKey>(key: K, params?: Record<string, unknown>): TranslationMap[K] => {
  return i18n.t(key, params) as TranslationMap[K];
};

/**
 * @deprecated Use _t instead.
 */
const __ = i18n.t;

/**
 * @deprecated Use _t instead.
 */
const _s = (key: string, params: Record<string, any> = null): string => i18n.t(key, params) as string;
const _a = (key: string, params: Record<string, any> = null): any[] => i18n.t(key, params) as any[];
export const _sTitle = (key: string, params: Record<string, any> = null): string =>
  _s(key, params).replace(/&amp;/g, '&');

const setLang = (lang: Lang) =>
  i18n.changeLanguage(lang, () => {
    if (!isServer && savedLanguage !== lang) {
      window.location.reload();
    }
  });

const getLang = () => {
  const validatedLang = languageSchema.safeParse(i18n.language);
  if (validatedLang.success) {
    return validatedLang.data;
  }
  return 'sv';
};
const getLanguages = (): Lang[] => Object.keys(LOCALE) as (keyof typeof LOCALE)[];
const getLocale = (): Locale => LOCALE[getLang()].code;

export { __, _a, _s, _t, getLang, getLanguages, getLocale, i18n, setLang };
